import React, { useMemo } from 'react';
import {
  StyleSheet,
  useWindowDimensions,
  Text,
  ScrollView,
  FlatList,
  View,
  Platform,
} from 'react-native';
import { JourneyType } from '../../screens/HiveReports/useJourneysQuery.gql';
import { HiveUserType } from '../../screens/MyHiveData/useHiveUsersQuery.gql';
import { EStageId, EStageType } from '../../../utils/journeysUtils';
import { ExcelIcon } from '../../../resources/svgComponents/ExcelIcon';
import { IconTextButton } from '../Button/IconTextButton';
import * as XLSX from 'xlsx';

let Share: any;
let RNFS: any;
let saveAs: any;

if (Platform.OS === 'web') {
  // Web only
  // @ts-ignore
  saveAs = require('file-saver').saveAs;
} else {
  // Native only
  Share = require('react-native-share').default;
  RNFS = require('react-native-fs');
}

type THiveReportsTableProps = {
  hiveUsers: HiveUserType[];
  hiveId: string;
  journeys: JourneyType[];
};

// ----------------- Helper functions -----------------

export const exportToExcel = async (rows: any[], filename = 'hive_reports.xlsx') => {
  if (!rows.length) return;

  // --- HEADER ROWS ---
  const headerRow1 = [
    'Student Name',
    'EFFICACY',
    '',
    '',
    '',
    '',
    'MATHEMATICAL MINDSET',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
  ];

  const headerRow2 = [
    '',
    'Buzz Game 1',
    'Buzz Game 2',
    'Buzz Game 3',
    'Overall Progress',
    'Avg Time / Answer',
    'Confidence',
    '',
    '',
    'Self Efficacy',
    '',
    '',
    'Attitude',
    '',
    '',
    'Growth Mindset (Effort)',
    '',
    '',
    'Growth Mindset (Ability)',
    '',
    '',
    'Reasoning (Process)',
    '',
    '',
    'Reasoning (Persistence)',
    '',
    '',
  ];

  // --- BODY DATA ---
  const bodyRows = rows.map(r => [
    r.name,
    r.buzz1,
    r.buzz2,
    r.buzz3,
    r.progress,
    r.avgTime,
    r.confidenceS1,
    r.confidenceS2,
    r.confidence,
    r.selfEfficacyS1,
    r.selfEfficacyS2,
    r.selfEfficacy,
    r.attitudeS1,
    r.attitudeS2,
    r.attitude,
    r.growthMindsetEffortS1,
    r.growthMindsetEffortS2,
    r.growthMindsetEffort,
    r.growthMindsetAbilityS1,
    r.growthMindsetAbilityS2,
    r.growthMindsetAbility,
    r.reasoningProcessS1,
    r.reasoningProcessS2,
    r.reasoningProcess,
    r.reasoningPersistenceS1,
    r.reasoningPersistenceS2,
    r.reasoningPersistence,
  ]);

  const worksheetData = [headerRow1, headerRow2, ...bodyRows];

  // --- CREATE SHEET ---
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // --- MERGE CELLS FOR GROUP HEADERS ---
  worksheet['!merges'] = [
    { s: { r: 0, c: 1 }, e: { r: 0, c: 5 } }, // EFFICACY
    { s: { r: 0, c: 6 }, e: { r: 0, c: 26 } }, // MATHEMATICAL MINDSET

    { s: { r: 1, c: 6 }, e: { r: 1, c: 8 } }, // Confidence
    { s: { r: 1, c: 9 }, e: { r: 1, c: 11 } }, // Self Efficacy
    { s: { r: 1, c: 12 }, e: { r: 1, c: 14 } }, // Attitude
    { s: { r: 1, c: 15 }, e: { r: 1, c: 17 } }, // Growth Mindset Effort
    { s: { r: 1, c: 18 }, e: { r: 1, c: 20 } }, // Growth Mindset Ability
    { s: { r: 1, c: 21 }, e: { r: 1, c: 23 } }, // Reasoning Process
    { s: { r: 1, c: 24 }, e: { r: 1, c: 26 } }, // Reasoning Persistence
  ];

  // --- WORKBOOK ---
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Hive Reports');

  if (Platform.OS === 'web') {
    // ✅ Web → file download
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, filename);
  } else {
    // ✅ Mobile → save + share
    const wbout = XLSX.write(workbook, { type: 'base64', bookType: 'xlsx' });
    const file = `${RNFS.DocumentDirectoryPath}/${filename}`;
    await RNFS.writeFile(file, wbout, 'base64');

    await Share.open({
      url: `file://${file}`,
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      failOnCancel: false,
    });
  }
};

const getScore = (journey: JourneyType | undefined, stageId: EStageId) => {
  const stageDetails = journey?.history.find(h => h.stageId === stageId)?.details;
  if (!stageDetails) return null;
  return JSON.parse(stageDetails).score;
};

const getAverageTime = (journey: JourneyType | undefined) => {
  const assessmentTimeEntries = journey?.history
    .filter(
      h =>
        h.type === EStageType.Assessment && h.details && JSON.parse(h.details).averageTimePerAnswer,
    )
    .map(h => (h.details ? JSON.parse(h.details).averageTimePerAnswer : undefined))
    .filter((v): v is number => v !== undefined);

  if (!assessmentTimeEntries || assessmentTimeEntries.length === 0) return '-';
  const averageTimePerAnswer =
    assessmentTimeEntries.reduce((a, b) => a + b, 0) / assessmentTimeEntries.length;
  return averageTimePerAnswer.toFixed(1);
};

const getSurveyResult = (journey: JourneyType | undefined, stageId: EStageId) => {
  const stageDetails = journey?.history.find(h => h.stageId === stageId)?.details;
  if (!stageDetails) return null;
  return JSON.parse(stageDetails);
};

const getSurveyProgress = (survey1: any, survey2: any) => {
  if (!survey1 || !survey2) return null;
  return {
    confidence: ((survey2.confidence - survey1.confidence) * 10).toFixed(0),
    selfEfficacy: ((survey2.selfEfficacy - survey1.selfEfficacy) * 10).toFixed(0),
    attitude: ((survey2.attitude - survey1.attitude) * 10).toFixed(0),
    growthMindsetEffort: ((survey2.growthMindsetEffort - survey1.growthMindsetEffort) * 10).toFixed(
      0,
    ),
    growthMindsetAbility: (
      (survey2.growthMindsetAbility - survey1.growthMindsetAbility) *
      10
    ).toFixed(0),
    reasoningProcess: ((survey2.reasoningProcess - survey1.reasoningProcess) * 10).toFixed(0),
    reasoningPersistence: (
      (survey2.reasoningPersistence - survey1.reasoningPersistence) *
      10
    ).toFixed(0),
  };
};

const buildRow = (user: HiveUserType, journeys: JourneyType[]) => {
  const journey = journeys.find(j => j.userId === user.id);

  const assessment1Score = getScore(journey, EStageId.Assessment1);
  const assessment2Score = getScore(journey, EStageId.Assessment2);
  const assessment3Score = getScore(journey, EStageId.Assessment3);

  const getOverallProgress = (): number | null => {
    if (assessment1Score === null) return null;
    if (assessment3Score !== null) return assessment3Score - assessment1Score;
    if (assessment2Score !== null) return assessment2Score - assessment1Score;
    return null;
  };

  const survey1 = getSurveyResult(journey, EStageId.Survey1);
  const survey2 = getSurveyResult(journey, EStageId.Survey2);
  const surveyProgress = getSurveyProgress(survey1, survey2);

  const defaultRow = {
    name: `${user.firstName} ${user.lastName}`,
    buzz1: '-',
    buzz2: '-',
    buzz3: '-',
    progress: '-',
    avgTime: '-',
    confidenceS1: '-',
    confidenceS2: '-',
    confidence: '-',
    selfEfficacyS1: '-',
    selfEfficacyS2: '-',
    selfEfficacy: '-',
    attitudeS1: '-',
    attitudeS2: '-',
    attitude: '-',
    growthMindsetEffortS1: '-',
    growthMindsetEffortS2: '-',
    growthMindsetEffort: '-',
    growthMindsetAbilityS1: '-',
    growthMindsetAbilityS2: '-',
    growthMindsetAbility: '-',
    reasoningProcessS1: '-',
    reasoningProcessS2: '-',
    reasoningProcess: '-',
    reasoningPersistenceS1: '-',
    reasoningPersistenceS2: '-',
    reasoningPersistence: '-',
  };

  if (!journey) return defaultRow;

  return {
    ...defaultRow,
    buzz1: assessment1Score !== null ? `${assessment1Score}%` : '-',
    buzz2: assessment2Score !== null ? `${assessment2Score}%` : '-',
    buzz3: assessment3Score !== null ? `${assessment3Score}%` : '-',
    progress: getOverallProgress() !== null ? `${getOverallProgress()}%` : '-',
    avgTime: getAverageTime(journey),
    confidenceS1: survey1?.confidence ?? '-',
    confidenceS2: survey2?.confidence ?? '-',
    confidence: surveyProgress ? `${surveyProgress.confidence}%` : '-',
    selfEfficacyS1: survey1?.selfEfficacy ?? '-',
    selfEfficacyS2: survey2?.selfEfficacy ?? '-',
    selfEfficacy: surveyProgress ? `${surveyProgress.selfEfficacy}%` : '-',
    attitudeS1: survey1?.attitude ?? '-',
    attitudeS2: survey2?.attitude ?? '-',
    attitude: surveyProgress ? `${surveyProgress.attitude}%` : '-',
    growthMindsetEffortS1: survey1?.growthMindsetEffort ?? '-',
    growthMindsetEffortS2: survey2?.growthMindsetEffort ?? '-',
    growthMindsetEffort: surveyProgress ? `${surveyProgress.growthMindsetEffort}%` : '-',
    growthMindsetAbilityS1: survey1?.growthMindsetAbility ?? '-',
    growthMindsetAbilityS2: survey2?.growthMindsetAbility ?? '-',
    growthMindsetAbility: surveyProgress ? `${surveyProgress.growthMindsetAbility}%` : '-',
    reasoningProcessS1: survey1?.reasoningProcess ?? '-',
    reasoningProcessS2: survey2?.reasoningProcess ?? '-',
    reasoningProcess: surveyProgress ? `${surveyProgress.reasoningProcess}%` : '-',
    reasoningPersistenceS1: survey1?.reasoningPersistence ?? '-',
    reasoningPersistenceS2: survey2?.reasoningPersistence ?? '-',
    reasoningPersistence: surveyProgress ? `${surveyProgress.reasoningPersistence}%` : '-',
  };
};

// --- Row Components ---
const EfficacyRow = ({
  row,
  index,
  isAverage,
}: {
  row: any;
  index: number;
  isAverage?: boolean;
}) => {
  const progressValue = parseFloat(String(row.progress).replace('%', ''));
  const progressStyle: any = [styles.cell, styles.border];
  if (!isNaN(progressValue)) {
    if (progressValue > 0) progressStyle.push(styles.positive);
    else if (progressValue < 0) progressStyle.push(styles.negative);
  }
  if (isAverage) progressStyle.push({ fontWeight: '700' });

  return (
    <View
      style={[
        styles.row,
        isAverage ? styles.averageRow : index % 2 === 0 ? styles.evenRow : styles.oddRow,
      ]}
    >
      <Text style={[styles.cell, styles.border, isAverage && { fontWeight: '700' }]}>
        {row.buzz1}
      </Text>
      <Text style={[styles.cell, styles.border, isAverage && { fontWeight: '700' }]}>
        {row.buzz2}
      </Text>
      <Text style={[styles.cell, styles.border, isAverage && { fontWeight: '700' }]}>
        {row.buzz3}
      </Text>
      <Text style={progressStyle}>{row.progress}</Text>
      <Text style={[styles.cell, styles.border, isAverage && { fontWeight: '700' }]}>
        {row.avgTime}
      </Text>
    </View>
  );
};

const NameRow = ({ row, index, isAverage }: { row: any; index: number; isAverage?: boolean }) => (
  <View
    style={[
      styles.row,
      isAverage ? styles.averageRow : index % 2 === 0 ? styles.evenRow : styles.oddRow,
      { backgroundColor: '#FFF5DD', borderWidth: 0 },
    ]}
  >
    <Text
      style={[
        styles.cell,
        styles.nameColumn,
        {
          flex: 2,
          fontWeight: isAverage ? '700' : '400',
          backgroundColor: '#FFF5DD',
          borderWidth: 0,
        },
      ]}
    >
      {row.name}
    </Text>
  </View>
);

const MindsetRow = ({
  row,
  index,
  isAverage,
}: {
  row: any;
  index: number;
  isAverage?: boolean;
}) => {
  const groups = [
    [row.confidenceS1, row.confidenceS2, row.confidence],
    [row.selfEfficacyS1, row.selfEfficacyS2, row.selfEfficacy],
    [row.attitudeS1, row.attitudeS2, row.attitude],
    [row.growthMindsetEffortS1, row.growthMindsetEffortS2, row.growthMindsetEffort],
    [row.growthMindsetAbilityS1, row.growthMindsetAbilityS2, row.growthMindsetAbility],
    [row.reasoningProcessS1, row.reasoningProcessS2, row.reasoningProcess],
    [row.reasoningPersistenceS1, row.reasoningPersistenceS2, row.reasoningPersistence],
  ];
  return (
    <View
      style={[
        styles.row,
        isAverage ? styles.averageRow : index % 2 === 0 ? styles.evenRow : styles.oddRow,
      ]}
    >
      {groups.map((g, colIdx) => (
        <View
          key={colIdx}
          style={{ flex: 1, flexDirection: 'row', borderWidth: 1, borderColor: '#FFE200' }}
        >
          {g.map((value, i) => {
            const isDelta = i === 2;
            let textStyle: any = [styles.cell, { flex: 1 }];
            if (isDelta && value !== '-') {
              const numeric = parseFloat(String(value).replace('%', ''));
              if (!isNaN(numeric)) {
                textStyle.push(
                  numeric > 0 ? styles.positive : numeric < 0 ? styles.negative : null,
                );
              }
            }
            if (isAverage) textStyle.push({ fontWeight: '700' });
            return (
              <Text key={i} style={textStyle}>
                {value}
              </Text>
            );
          })}
        </View>
      ))}
    </View>
  );
};

// --- Main Component ---
export default function HiveReportsTable(props: THiveReportsTableProps) {
  const { journeys, hiveUsers } = props;
  const { width } = useWindowDimensions();
  const sizeLimit = width < 800;

  const rows = useMemo(
    () => hiveUsers.map(user => buildRow(user, journeys)),
    [hiveUsers, journeys],
  );
  const averageRow = useMemo(() => {
    if (rows.length === 0) return null;
    const avg = <T extends keyof (typeof rows)[0]>(
      key: T,
      addPercent = false,
      forceDecimal = false,
    ) => {
      const nums = rows
        .map(r => parseFloat(String(r[key]).replace('%', '')))
        .filter(v => !isNaN(v));
      if (nums.length === 0) return '-';
      const raw = nums.reduce((a, b) => a + b, 0) / nums.length;
      const val = forceDecimal ? raw.toFixed(1) : Math.round(raw).toString();
      return addPercent ? `${val}%` : val;
    };
    return {
      name: 'Average',
      buzz1: avg('buzz1', true),
      buzz2: avg('buzz2', true),
      buzz3: avg('buzz3', true),
      progress: avg('progress', true),
      avgTime: avg('avgTime', false, true),
      confidenceS1: avg('confidenceS1'),
      confidenceS2: avg('confidenceS2'),
      confidence: avg('confidence', true),
      selfEfficacyS1: avg('selfEfficacyS1'),
      selfEfficacyS2: avg('selfEfficacyS2'),
      selfEfficacy: avg('selfEfficacy', true),
      attitudeS1: avg('attitudeS1'),
      attitudeS2: avg('attitudeS2'),
      attitude: avg('attitude', true),
      growthMindsetEffortS1: avg('growthMindsetEffortS1'),
      growthMindsetEffortS2: avg('growthMindsetEffortS2'),
      growthMindsetEffort: avg('growthMindsetEffort', true),
      growthMindsetAbilityS1: avg('growthMindsetAbilityS1'),
      growthMindsetAbilityS2: avg('growthMindsetAbilityS2'),
      growthMindsetAbility: avg('growthMindsetAbility', true),
      reasoningProcessS1: avg('reasoningProcessS1'),
      reasoningProcessS2: avg('reasoningProcessS2'),
      reasoningProcess: avg('reasoningProcess', true),
      reasoningPersistenceS1: avg('reasoningPersistenceS1'),
      reasoningPersistenceS2: avg('reasoningPersistenceS2'),
      reasoningPersistence: avg('reasoningPersistence', true),
    };
  }, [rows]);

  return (
    <View style={[styles.container, sizeLimit && { paddingHorizontal: 2 }]}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: sizeLimit ? 'center' : 'flex-end',
          width: '100%',
          marginBottom: 10,
        }}
      >
        <IconTextButton
          icon={<ExcelIcon height={30} width={30} />}
          onPress={() => exportToExcel([...(rows || []), ...(averageRow ? [averageRow] : [])])}
          containerStyle={{ backgroundColor: '#FFCF53', width: 49 }}
        />
      </View>
      <View style={[styles.tableContainer, sizeLimit && { padding: 10 }]}>
        <ScrollView horizontal>
          <View style={styles.tableWrapper}>
            {/* Left side - NAME */}
            <View style={[styles.subTableName, { borderWidth: 0 }]}>
              <View
                style={[
                  styles.row,
                  styles.header,
                  { height: 84, backgroundColor: '#FFF5DD', borderWidth: 0 },
                ]}
              >
                <Text style={[styles.cell, styles.headerText, { flex: 2 }]}>Student Name</Text>
              </View>
              {averageRow && <NameRow row={averageRow} index={-1} isAverage />}
              <FlatList
                data={rows}
                keyExtractor={(item, index) => `${item.name}-${index}`}
                renderItem={({ item, index }) => <NameRow row={item} index={index} />}
              />
            </View>

            {/* Middle - EFFICACY */}
            <View style={[styles.subTableEfficacy]}>
              <View style={[styles.row, styles.header]}>
                <Text style={[styles.cell, styles.headerText, { flex: 5 }]}>EFFICACY</Text>
              </View>
              <View style={[styles.row, styles.header]}>
                <Text style={[styles.cell, styles.borderHeader]}>Buzz Game 1</Text>
                <Text style={[styles.cell, styles.borderHeader]}>Buzz Game 2</Text>
                <Text style={[styles.cell, styles.borderHeader]}>Buzz Game 3</Text>
                <Text style={[styles.cell, styles.borderHeader]}>Overall Progress</Text>
                <Text style={[styles.cell, styles.borderHeader]}>Avg Time / Answer</Text>
              </View>
              {averageRow && <EfficacyRow row={averageRow} index={-1} isAverage />}
              <FlatList
                data={rows}
                keyExtractor={(item, index) => `${item.name}-${index}`}
                renderItem={({ item, index }) => <EfficacyRow row={item} index={index} />}
              />
            </View>

            {/* Right side - MATHEMATICAL MINDSET */}
            <View style={styles.subTableMathematical}>
              <View style={[styles.row, styles.header]}>
                <Text style={[styles.cell, styles.headerText, { flex: 5 }]}>
                  MATHEMATICAL MINDSET
                </Text>
              </View>
              <View style={[styles.row, styles.header]}>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>Confidence</Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>Self Efficacy</Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>Attitude</Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>
                  Growth Mindset (Effort)
                </Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>
                  Growth Mindset (Ability)
                </Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>
                  Reasoning (Process)
                </Text>
                <Text style={[styles.cell, styles.borderHeader, { flex: 1 }]}>
                  Reasoning (Persistence)
                </Text>
              </View>
              {averageRow && <MindsetRow row={averageRow} index={-1} isAverage />}
              <FlatList
                data={rows}
                keyExtractor={(item, index) => `${item.name}-${index}`}
                renderItem={({ item, index }) => <MindsetRow row={item} index={index} />}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 1500,
    paddingHorizontal: 54,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    padding: 20,
    borderColor: '#FFCF53',
    borderTopWidth: 2,
    borderBottomWidth: 6,
    borderRightWidth: 3,
    borderLeftWidth: 3,
  },
  tableWrapper: { flexDirection: 'row' },
  subTableName: { backgroundColor: '#FFF5DD', width: 200 },
  subTableEfficacy: {
    borderWidth: 1,
    borderColor: '#FFE200',
    backgroundColor: '#FFF8E7',
    marginRight: 15,
  },
  subTableMathematical: { borderWidth: 1, borderColor: '#FFE200', backgroundColor: '#FFF8E7' },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#FFE200',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42, // will make sure fixed height will center text vertically same for all tables
  },
  header: { backgroundColor: '#FEF3D6', height: 42 },
  headerText: {
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: 'Secular One',
    color: '#453B3B',
  },
  cell: {
    flex: 1,
    padding: 8,
    textAlign: 'center', // horizontal center
    textAlignVertical: 'center', // vertical center (Android)
    includeFontPadding: false, // helps with vertical alignment
    color: '#453B3B',
  },
  nameColumn: { backgroundColor: '#FEF3D6', color: '#333333' },
  evenRow: { backgroundColor: '#FEE9B2' },
  oddRow: { backgroundColor: '#FEE9B2' },
  averageRow: { backgroundColor: '#FFE599' },
  positive: { color: 'green' },
  negative: { color: 'red' },
  border: { borderWidth: 1, borderColor: '#FFE200' },
  borderHeader: {
    borderWidth: 1,
    borderColor: '#FFE200',
    borderTopWidth: 0,
    fontWeight: '700',
    fontFamily: 'Secular One',
  },
});
